import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';

interface TenantListItem {
  tenant_id: string;
  name: string;
  status: string;
  user_count: number;
  created_at: string | null;
}

export default function Tenants() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: TenantListItem[] }>('/api/tenants'),
  });

  const [panel, set_panel] = useState<'none' | 'tenant' | 'user'>('none');

  function on_created() {
    qc.invalidateQueries({ queryKey: ['tenants'] });
    set_panel('none');
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          <h1 style={{ marginBottom: 0 }}>Tenants</h1>
          <p className="muted" style={{ marginTop: 4, marginBottom: 24 }}>
            Every organization in the platform Firestore.
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            className="secondary"
            onClick={() => set_panel((p) => (p === 'tenant' ? 'none' : 'tenant'))}
          >
            Add tenant
          </button>
          <button onClick={() => set_panel((p) => (p === 'user' ? 'none' : 'user'))}>
            Add user
          </button>
        </div>
      </div>

      {panel === 'tenant' && <AddTenantForm on_created={on_created} />}
      {panel === 'user' && <AddUserForm on_created={on_created} />}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
        {error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load: {error instanceof ApiError ? error.reason : String(error)}
          </p>
        )}
        {data && (
          <DataTable
            rows={data.tenants}
            columns={[
              {
                header: 'Name',
                cell: (t) => (
                  <Link to={`/tenants/${t.tenant_id}`}>
                    <strong>{t.name}</strong>
                  </Link>
                ),
              },
              { header: 'ID', cell: (t) => <code style={{ fontSize: 12 }}>{t.tenant_id}</code> },
              { header: 'Users', cell: (t) => t.user_count },
              { header: 'Status', cell: (t) => t.status },
              {
                header: 'Created',
                cell: (t) =>
                  t.created_at ? new Date(t.created_at).toLocaleDateString() : '—',
              },
            ]}
            empty_message="No tenants yet."
          />
        )}
      </div>
    </div>
  );
}

function AddTenantForm({ on_created }: { on_created: () => void }) {
  const [name, set_name] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    set_busy(true);
    set_error(null);
    try {
      await api.post('/api/seed/tenant', { name: name.trim() });
      on_created();
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_busy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card" style={{ marginBottom: 16, display: 'grid', gap: 12, maxWidth: 480 }}>
      <h3 style={{ margin: 0 }}>Add tenant</h3>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Tenant name</span>
        <input value={name} onChange={(e) => set_name(e.target.value)} required maxLength={120} />
      </label>
      {error && <div className="error">{error}</div>}
      <div>
        <button type="submit" disabled={busy}>
          {busy ? 'Creating...' : 'Create tenant'}
        </button>
      </div>
    </form>
  );
}

function AddUserForm({ on_created }: { on_created: () => void }) {
  const [first, set_first] = useState('');
  const [last, set_last] = useState('');
  const [email, set_email] = useState('');
  const [company, set_company] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    set_busy(true);
    set_error(null);
    try {
      await api.post('/api/seed/user', {
        first_name: first.trim(),
        last_name: last.trim(),
        email: email.trim(),
        company: company.trim(),
      });
      on_created();
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_busy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card" style={{ marginBottom: 16, display: 'grid', gap: 12, maxWidth: 540 }}>
      <h3 style={{ margin: 0 }}>Add user</h3>
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Resolves the tenant by company name + email domain. To add a user to a specific
        existing tenant, use "Add user to this tenant" on the tenant's page.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>First name</span>
          <input value={first} onChange={(e) => set_first(e.target.value)} required maxLength={80} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Last name</span>
          <input value={last} onChange={(e) => set_last(e.target.value)} required maxLength={80} />
        </label>
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Email</span>
        <input type="email" value={email} onChange={(e) => set_email(e.target.value)} required maxLength={254} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Company</span>
        <input value={company} onChange={(e) => set_company(e.target.value)} required maxLength={120} />
      </label>
      {error && <div className="error">{error}</div>}
      <div>
        <button type="submit" disabled={busy}>
          {busy ? 'Creating...' : 'Create user'}
        </button>
      </div>
    </form>
  );
}
